import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, DollarSign, Heart, Users, Shield } from 'lucide-react';

const SavedJobsTable = ({ jobs, onUnsave }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(10);

  const visibleJobs = jobs.slice(0, visibleCount);
  const hasMore = jobs.length > visibleCount;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 10);
  };

  return (
    <div className="space-y-4">
      <Card>
        <div>
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="py-3 px-4 text-left font-semibold text-sm">{t('tables.jobTitle')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden lg:table-cell">{t('tables.employer')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden md:table-cell">{t('tables.location')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">{t('tables.salary')}</th>
                <th className="py-3 px-4 text-center font-semibold text-sm">{t('tables.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibleJobs.map((job) => {
                const employer = job.employer || {};

                return (
                  <tr 
                    key={job.id} 
                    className="hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    data-testid={`saved-job-row-${job.id}`}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium mb-1">{job.title || 'Job Title'}</div>
                        {/* Mobile: Show employer and location */}
                        <div className="flex flex-col gap-1 text-xs text-muted-foreground lg:hidden">
                          <span>{employer.business_name || employer.name || 'Employer'}</span>
                          <div className="flex items-center gap-1">
                            <MapPin size={12} />
                            <span>{job.location || 'Location'}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm hidden lg:table-cell">
                      <div className="flex items-center gap-2">
                        <span>{employer.business_name || employer.name || 'Employer'}</span>
                        {employer.verified && (
                          <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <MapPin size={14} />
                        {job.location || '-'}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground hidden sm:table-cell">
                      {job.salary_min && job.salary_max ? (
                        <div className="flex items-center gap-1">
                          <span className="font-bold">₱</span>
                          <span>{job.salary_min.toLocaleString()} - ₱{job.salary_max.toLocaleString()}</span>
                        </div>
                      ) : '-'}

                      {/* Quick Filter Badges */}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {job.positions_available > 1 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                            <Users size={10} /> Hiring {job.positions_available}
                          </span>
                        )}
                        {job.urgent_hiring && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
                            🔴 Urgent
                          </span>
                        )}
                        {job.no_experience_needed && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
                            ⭐ No Exp
                          </span>
                        )}
                        {job.with_housing && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                            🏠 Housing
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnsave(job.id);
                          }}
                          data-testid={`unsave-job-${job.id}`}
                        >
                          <Heart size={20} className="fill-red-500 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            className="rounded-xl"
            data-testid="load-more-saved-jobs"
          >
            {t('tables.loadMore')} ({jobs.length - visibleCount} {t('tables.remaining')})
          </Button>
        </div>
      )}
    </div>
  );
};

export default SavedJobsTable;
